/**
 * Web stand-in for `react-native-image-picker`.
 *
 * Keeps the exact callback API the app and zen-core-elements already use:
 *   launchImageLibrary(options, callback)
 *   launchCamera(options, callback)
 *
 * `launchImageLibrary` opens the browser file chooser; `launchCamera` opens a
 * getUserMedia capture overlay and falls back to the file chooser when the
 * machine has no camera.
 */

import {openFileDialog, readAsBase64, readImageSize, PickedWebFile} from './webFileInput';
import {capturePhoto, isCameraAvailable} from './webCameraCapture';

export type Asset = {
  uri?: string;
  fileName?: string;
  fileSize?: number;
  type?: string;
  width?: number;
  height?: number;
  base64?: string;
  duration?: number;
  /** Web-only escape hatch: the underlying File/Blob, handy for uploads. */
  webFile?: File | Blob;
};

export type ImagePickerResponse = {
  didCancel?: boolean;
  errorCode?: string;
  errorMessage?: string;
  assets?: Asset[];
};

export type ImageLibraryOptions = {
  mediaType?: 'photo' | 'video' | 'mixed';
  selectionLimit?: number;
  includeBase64?: boolean;
  [key: string]: any;
};

export type CameraOptions = ImageLibraryOptions;

type Callback = (response: ImagePickerResponse) => void;

function acceptFor(mediaType?: string): string {
  if (mediaType === 'video') {
    return 'video/*';
  }
  if (mediaType === 'mixed') {
    return 'image/*,video/*';
  }
  return 'image/*';
}

async function toAsset(picked: PickedWebFile, includeBase64?: boolean): Promise<Asset> {
  const asset: Asset = {
    uri: picked.uri,
    fileName: picked.name,
    fileSize: picked.size,
    type: picked.type,
    webFile: picked.file,
  };

  if (picked.type.startsWith('image/')) {
    const size = await readImageSize(picked.uri);
    asset.width = size.width;
    asset.height = size.height;
  }
  if (includeBase64) {
    asset.base64 = await readAsBase64(picked.file);
  }
  return asset;
}

export async function launchImageLibrary(
  options: ImageLibraryOptions = {},
  callback?: Callback,
): Promise<ImagePickerResponse> {
  let response: ImagePickerResponse;
  try {
    const selectionLimit = options.selectionLimit == null ? 1 : options.selectionLimit;
    const picked = await openFileDialog({
      accept: acceptFor(options.mediaType),
      multiple: selectionLimit !== 1,
    });

    if (picked.length === 0) {
      response = {didCancel: true};
    } else {
      const limited = selectionLimit > 0 ? picked.slice(0, selectionLimit) : picked;
      response = {assets: await Promise.all(limited.map(p => toAsset(p, options.includeBase64)))};
    }
  } catch (error: any) {
    response = {errorCode: 'others', errorMessage: error?.message ?? String(error)};
  }

  if (callback) {
    callback(response);
  }
  return response;
}

export async function launchCamera(
  options: CameraOptions = {},
  callback?: Callback,
): Promise<ImagePickerResponse> {
  // No camera on this machine - the file chooser is the sensible desktop path.
  if (!isCameraAvailable()) {
    return launchImageLibrary(options, callback);
  }

  let response: ImagePickerResponse;
  try {
    const photo = await capturePhoto();
    if (!photo) {
      response = {didCancel: true};
    } else {
      const asset: Asset = {
        uri: photo.uri,
        fileName: photo.name,
        fileSize: photo.blob.size,
        type: photo.type,
        width: photo.width,
        height: photo.height,
        webFile: photo.blob,
      };
      if (options.includeBase64) {
        asset.base64 = await readAsBase64(new File([photo.blob], photo.name, {type: photo.type}));
      }
      response = {assets: [asset]};
    }
  } catch (error: any) {
    response = {errorCode: 'camera_unavailable', errorMessage: error?.message ?? String(error)};
  }

  if (callback) {
    callback(response);
  }
  return response;
}

export default {launchCamera, launchImageLibrary};
